/* eslint-disable max-lines */
import { Team } from '@litlingo/client';
import {
  addExcludedInsight,
  addExcludedPrevent,
  addExcludedRoles,
  addExcludedTeams,
  addIncludedInsight,
  addIncludedPrevent,
  addIncludedRoles,
  addIncludedTeams,
  removeExcludedInsight,
  removeExcludedPrevent,
  removeExcludedRoles,
  removeExcludedTeams,
  removeIncludedInsight,
  removeIncludedPrevent,
  removeIncludedRoles,
  removeIncludedTeams,
} from 'actions/userPermissionsPage';
import Permissions from 'components/Permissions';
import TeamSelectRedesign from 'components/TeamsManager/TeamSelectRedesign';
import { selectableUserTypes } from 'constants/userRoles';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'selectors/auth';
import {
  getExcludedInsight,
  getExcludedPrevent,
  getExcludedRoles,
  getExcludedTeams,
  getIncludedInsight,
  getIncludedPrevent,
  getIncludedRoles,
  getIncludedTeams,
} from 'selectors/userPermissionsPage';
import PermissionsSelect from './PermissionsSelect';

const insightOptions = ['email', 'chat'];
const preventOptions = ['email', 'chat'];

type ComponentProps = {
  singleUser?: boolean;
};

const PermissionsSelections: React.FC<ComponentProps> = ({ singleUser = true }) => {
  const dispatch = useDispatch();

  const user = useSelector(getUser);

  const includedTR = useSelector(getIncludedTeams);
  const excludedTR = useSelector(getExcludedTeams);

  // @ts-ignore
  const includedTeams = includedTR.filter((t) => t.kind === 'division');
  // @ts-ignore
  const excludedTeams = excludedTR.filter((t) => t.kind === 'division');

  // @ts-ignore
  const includedRegions = includedTR.filter((t) => t.kind === 'region');
  // @ts-ignore
  const excludedRegions = excludedTR.filter((t) => t.kind === 'region');

  // @ts-ignore
  const includedManagementLevels = includedTR.filter((t) => t.kind === 'management-level');
  // @ts-ignore
  const excludedManagementLevels = excludedTR.filter((t) => t.kind === 'management-level');

  const isSuperAdmin = user.roles.includes('super-admin');

  const handleAddTeams = (teams: Team[], exclude = false): void => {
    if (exclude) {
      dispatch(addExcludedTeams(teams));
    } else {
      dispatch(addIncludedTeams(teams));
    }
  };

  const handleRemoveTeams = (teams: Team[], exclude = false): void => {
    if (exclude) {
      dispatch(removeExcludedTeams(teams));
    } else {
      dispatch(removeIncludedTeams(teams));
    }
  };

  return (
    <div className="w-full flex flex-row gap-4">
      <div className="w-1/2 flex flex-col gap-2">
        <PermissionsSelect
          includedItemsSelector={getIncludedRoles}
          excludedItemsSelector={singleUser ? undefined : getExcludedRoles}
          addIncludedAction={addIncludedRoles}
          removeIncludedAction={removeIncludedRoles}
          addExcludedAction={singleUser ? undefined : addExcludedRoles}
          removeExcludedAction={singleUser ? undefined : removeExcludedRoles}
          options={Object.values(selectableUserTypes)
            .filter((r) => r.label !== 'Litlingo Admin' || isSuperAdmin)
            .map((r) => r.label)}
          title="Roles"
          showLabels={false}
        />
        <TeamSelectRedesign
          allowExclude={!singleUser}
          includedTeams={includedTeams}
          addIncludedTeams={handleAddTeams}
          removeIncludedTeams={handleRemoveTeams}
          excludedTeams={singleUser ? [] : excludedTeams}
          addExcludedTeams={(teams): void => handleAddTeams(teams, true)}
          removeExcludedTeams={(teams): void => handleRemoveTeams(teams, true)}
          showLabels={false}
        />
        <TeamSelectRedesign
          allowExclude={!singleUser}
          includedTeams={includedRegions}
          addIncludedTeams={handleAddTeams}
          removeIncludedTeams={handleRemoveTeams}
          excludedTeams={singleUser ? [] : excludedRegions}
          addExcludedTeams={(teams): void => handleAddTeams(teams, true)}
          removeExcludedTeams={(teams): void => handleRemoveTeams(teams, true)}
          showLabels={false}
          kind="region"
        />
      </div>
      <div className="w-1/2 flex flex-col gap-2">
        <TeamSelectRedesign
          allowExclude={!singleUser}
          includedTeams={includedManagementLevels}
          addIncludedTeams={handleAddTeams}
          removeIncludedTeams={handleRemoveTeams}
          excludedTeams={singleUser ? [] : excludedManagementLevels}
          addExcludedTeams={(teams): void => handleAddTeams(teams, true)}
          removeExcludedTeams={(teams): void => handleRemoveTeams(teams, true)}
          showLabels={false}
          kind="management-level"
        />
        <Permissions action="users.editAdvanced">
          <PermissionsSelect
            includedItemsSelector={getIncludedInsight}
            excludedItemsSelector={singleUser ? undefined : getExcludedInsight}
            addIncludedAction={addIncludedInsight}
            removeIncludedAction={removeIncludedInsight}
            addExcludedAction={singleUser ? undefined : addExcludedInsight}
            removeExcludedAction={singleUser ? undefined : removeExcludedInsight}
            options={insightOptions}
            title="Insight"
            showLabels={false}
          />
        </Permissions>

        <Permissions action="users.editAdvanced">
          <PermissionsSelect
            includedItemsSelector={getIncludedPrevent}
            excludedItemsSelector={singleUser ? undefined : getExcludedPrevent}
            addIncludedAction={addIncludedPrevent}
            removeIncludedAction={removeIncludedPrevent}
            addExcludedAction={singleUser ? undefined : addExcludedPrevent}
            removeExcludedAction={singleUser ? undefined : removeExcludedPrevent}
            options={preventOptions}
            title="Prevent"
            showLabels={false}
          />
        </Permissions>
      </div>
    </div>
  );
};

export default PermissionsSelections;
